import { buildStartOsUpdateCommandPayload, type StartOsUpdateCommandPayloadInput } from "./startOsUpdateCommand";

const emteriaUpdatePackageId = "com.emteria.update";

export type LastStateCarrier = {
    lastState?: API.DeviceState | null;
};

// Uses API-reported `versionCode` for com.emteria.update (device truth), not versionName strings — avoids semver parsing drift.

export const getEmteriaUpdateVersionCode = (lastState: API.DeviceState | null | undefined): number | undefined => {
    return lastState?.packages?.find((p) => p.package === emteriaUpdatePackageId)?.versionCode;
};

// Lowest `versionCode` among devices that report Emteria Update (for group policy / group commands).
// Devices without the package are skipped.

export const getMinimumEmteriaUpdateVersionCodeAcrossDevices = (devices: LastStateCarrier[]): number | undefined => {
    let minimum: number | undefined;

    for (const device of devices) {
        const versionCode = getEmteriaUpdateVersionCode(device.lastState);
        if (versionCode === undefined) {
            continue;
        }
        if (minimum === undefined || versionCode < minimum) {
            minimum = versionCode;
        }
    }

    return minimum;
};

// `versionCode` for command preparation: single device uses its installed build; otherwise the minimum across group.

export const getEffectiveEmteriaUpdateVersionCode = (options: {
    device?: LastStateCarrier | null;
    groupDevices?: LastStateCarrier[] | null;
}): number | null => {
    if (options.device) {
        return getEmteriaUpdateVersionCode(options.device.lastState) ?? null;
    }
    return getMinimumEmteriaUpdateVersionCodeAcrossDevices(options.groupDevices ?? []) ?? null;
};

type CommandPayload = Record<string, unknown>;

export const prepareCommandPayload = (
    commandType: string,
    payload: CommandPayload,
    emteriaUpdateVersionCode: number | null,
): Record<string, unknown> => {
    if (commandType === "startOsUpdate") {
        return buildStartOsUpdateCommandPayload(payload as StartOsUpdateCommandPayloadInput, emteriaUpdateVersionCode);
    }

    return payload;
};
